import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Room, RoomEvent, Track } from 'livekit-client';
import API from '../../Network/API';
import './LiveKitCall.css';

/* ───── Inline SVG Icons (Bootstrap Icons not loaded in project) ───── */
const MicIcon = () => (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
        <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
        <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z"/>
    </svg>
);
const MicMuteIcon = () => (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
        <path d="M13 8c0 .564-.094 1.107-.266 1.613l-.814-.814A4.02 4.02 0 0 0 12 8V7a.5.5 0 0 1 1 0v1zm-5 4c.818 0 1.578-.245 2.212-.667l.718.719a4.973 4.973 0 0 1-2.43.923V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 1 0v1a4 4 0 0 0 4 4zm3-9v4.879l-1-1V3a2 2 0 0 0-3.997-.118l-.845-.845A3.001 3.001 0 0 1 11 3z"/>
        <path d="m9.486 10.607-.748-.748A2 2 0 0 1 6 8V6.121l-1-1V8a3 3 0 0 0 4.486 2.607zm-7.84-9.253 12 12 .708-.708-12-12-.708.708z"/>
    </svg>
);
const CameraIcon = () => (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2V5z"/>
    </svg>
);
const CameraOffIcon = () => (
    <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M10.961 12.365a1.99 1.99 0 0 0 .522-1.103l3.11 1.382A1 1 0 0 0 16 11.731V4.269a1 1 0 0 0-1.406-.913l-3.111 1.382A2 2 0 0 0 9.5 3H4.272l6.69 9.365zm-10.114-9A2.001 2.001 0 0 0 0 5v6a2 2 0 0 0 2 2h5.728L.847 3.366zm9.746 11.925-10-14 .814-.58 10 14-.814.58z"/>
    </svg>
);
const PhoneEndIcon = () => (
    <svg width="28" height="28" fill="currentColor" viewBox="0 0 16 16">
        <path d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
        <path d="M11 1.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V2.707l-3.45 3.45-.707-.708L14.293 2H11.5a.5.5 0 0 1-.5-.5z" transform="rotate(45 12 4)"/>
    </svg>
);
const PersonIcon = () => (
    <svg width="56" height="56" fill="currentColor" viewBox="0 0 16 16">
        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
    </svg>
);

/**
 * LiveKit-powered 1:1 Call page.
 *
 * Query params:
 * - room: LiveKit room name
 * - token: LiveKit JWT token
 * - url: LiveKit WebSocket URL
 * - audioOnly: '1' = audio only, '0' = video call
 * - callingId: calling_logs row ID (for end call API)
 * - callerName: name of the other party
 */
export default function LiveKitCall() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const roomName = searchParams.get('room');
    const token = searchParams.get('token');
    const livekitUrl = searchParams.get('url');
    const audioOnly = searchParams.get('audioOnly') === '1';
    const callingId = searchParams.get('callingId');
    const callerName = searchParams.get('callerName') || 'Unknown';

    const roomRef = useRef(null);
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const endGraceTimerRef = useRef(null);
    const remoteJoinedRef = useRef(false);

    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(true);
    const [isMuted, setIsMuted] = useState(false);
    const [isCameraOff, setIsCameraOff] = useState(audioOnly);
    const [callDuration, setCallDuration] = useState('00:00');
    const [remoteJoined, setRemoteJoined] = useState(false);
    const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

    const callStartTimeRef = useRef(null);
    const timerRef = useRef(null);

    // Connect to LiveKit room
    useEffect(() => {
        if (!token || !livekitUrl || !roomName) {
            console.error('Missing LiveKit connection params');
            navigate('/');
            return;
        }

        const room = new Room({
            adaptiveStream: true,
            dynacast: true,
            videoCaptureDefaults: {
                resolution: { width: 1280, height: 720, frameRate: 30 },
            },
            publishDefaults: {
                simulcast: true,
                videoCodec: 'vp8',
            },
        });
        roomRef.current = room;

        room.on(RoomEvent.Connected, () => {
            setIsConnected(true);
            setIsConnecting(false);
        });

        room.on(RoomEvent.Disconnected, () => {
            handleEndCall();
        });

        room.on(RoomEvent.ParticipantConnected, () => {
            // A participant joined (or re-joined after a same-identity replace).
            // Cancel any pending end-call grace timer.
            if (endGraceTimerRef.current) {
                clearTimeout(endGraceTimerRef.current);
                endGraceTimerRef.current = null;
            }
            remoteJoinedRef.current = true;
            setRemoteJoined(true);
            startDurationTimer();
        });

        room.on(RoomEvent.ParticipantDisconnected, () => {
            // Don't end the call instantly — LiveKit fires ParticipantDisconnected
            // when a same-identity client replaces an existing one (e.g. the user
            // accepts on web while their iOS app held a pre-connect to the room),
            // and on transient network blips. Wait briefly to see if the room
            // still has remote participants before tearing down the call.
            if (endGraceTimerRef.current) clearTimeout(endGraceTimerRef.current);
            endGraceTimerRef.current = setTimeout(() => {
                endGraceTimerRef.current = null;
                const r = roomRef.current;
                // If the room is gone or still has at least one other participant,
                // don't end. Only end when we've truly been left alone.
                if (!r) return;
                if (r.remoteParticipants && r.remoteParticipants.size > 0) return;
                // If the remote never joined yet, the original setTimeout in
                // useEffect will handle navigation; only end an established call here.
                if (!remoteJoinedRef.current) return;
                handleEndCall();
            }, 4000);
        });

        room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
            if (track.kind === Track.Kind.Video) {
                setHasRemoteVideo(true);
                if (remoteVideoRef.current) {
                    track.attach(remoteVideoRef.current);
                }
            } else if (track.kind === Track.Kind.Audio) {
                const audioEl = track.attach();
                document.body.appendChild(audioEl);
            }
        });

        room.on(RoomEvent.TrackUnsubscribed, (track) => {
            if (track.kind === Track.Kind.Video) {
                setHasRemoteVideo(false);
            }
            track.detach();
        });

        // Connect
        room.connect(livekitUrl, token).then(async () => {
            await room.localParticipant.setMicrophoneEnabled(true);

            if (!audioOnly) {
                await room.localParticipant.setCameraEnabled(true);
                const localVideoTrack = room.localParticipant.getTrackPublication(Track.Source.Camera);
                if (localVideoTrack?.track && localVideoRef.current) {
                    localVideoTrack.track.attach(localVideoRef.current);
                }
            }

            // Check if remote already in room
            room.remoteParticipants.forEach((participant) => {
                remoteJoinedRef.current = true;
                setRemoteJoined(true);
                startDurationTimer();
                participant.getTrackPublications().forEach((pub) => {
                    if (pub.track) {
                        if (pub.track.kind === Track.Kind.Video && remoteVideoRef.current) {
                            pub.track.attach(remoteVideoRef.current);
                        } else if (pub.track.kind === Track.Kind.Audio) {
                            const audioEl = pub.track.attach();
                            document.body.appendChild(audioEl);
                        }
                    }
                });
            });
        }).catch((err) => {
            console.error('Failed to connect to LiveKit:', err);
            setIsConnecting(false);
        });

        return () => {
            room.disconnect();
            if (timerRef.current) clearInterval(timerRef.current);
            if (endGraceTimerRef.current) clearTimeout(endGraceTimerRef.current);
        };
    }, []);

    const startDurationTimer = () => {
        if (callStartTimeRef.current) return;
        callStartTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
            const mins = String(Math.floor(elapsed / 60)).padStart(2, '0');
            const secs = String(elapsed % 60).padStart(2, '0');
            setCallDuration(`${mins}:${secs}`);
        }, 1000);
    };

    const handleEndCall = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        if (endGraceTimerRef.current) {
            clearTimeout(endGraceTimerRef.current);
            endGraceTimerRef.current = null;
        }
        if (callingId) {
            API.post(`v1/calls/${callingId}/end`, {}).catch(() => {});
        }
        if (roomRef.current) {
            roomRef.current.disconnect();
            roomRef.current = null;
        }
        navigate('/');
    }, [callingId, navigate]);

    const toggleMute = async () => {
        if (!roomRef.current) return;
        const newMuted = !isMuted;
        await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
        setIsMuted(newMuted);
    };

    const toggleCamera = async () => {
        if (!roomRef.current) return;
        const newOff = !isCameraOff;
        await roomRef.current.localParticipant.setCameraEnabled(!newOff);
        setIsCameraOff(newOff);
    };

    // Re-attach local video track whenever camera is toggled on
    useEffect(() => {
        if (!isCameraOff && roomRef.current && localVideoRef.current) {
            // Small delay to let LiveKit publish the track
            const timer = setTimeout(() => {
                const pub = roomRef.current?.localParticipant?.getTrackPublication(Track.Source.Camera);
                if (pub?.track && localVideoRef.current) {
                    pub.track.attach(localVideoRef.current);
                }
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isCameraOff]);

    // Get initials from caller name
    const initials = callerName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return (
        <div className="lk-call-container">
            {/* Connecting overlay */}
            {isConnecting && (
                <div className="lk-call-connecting">
                    <div className="lk-call-spinner"></div>
                    <p className="lk-call-connecting-text">Connecting to {callerName}...</p>
                </div>
            )}

            {/* Video elements — always in DOM, hidden when not active */}
            <div className="lk-call-video-area">
                <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    className="lk-call-remote-video"
                    style={{ display: hasRemoteVideo ? 'block' : 'none' }}
                />
                <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="lk-call-local-video"
                    style={{ display: !isCameraOff ? 'block' : 'none' }}
                />
                {!hasRemoteVideo && (
                    <div className="lk-call-audio-view">
                        <div className={`lk-call-avatar ${remoteJoined ? 'connected' : 'ringing'}`}>
                            <span className="lk-call-initials">{initials}</span>
                        </div>
                        <h2 className="lk-call-caller-name">{callerName}</h2>
                        <p className="lk-call-status">
                            {remoteJoined ? callDuration : 'Connecting...'}
                        </p>
                        {!remoteJoined && (
                            <div className="lk-call-wave-bars">
                                <span></span><span></span><span></span><span></span><span></span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Top bar */}
            <div className="lk-call-top-bar">
                <div className="lk-call-status-badge">
                    <span className={`lk-call-dot ${isConnected ? 'connected' : ''}`}></span>
                    <span>{remoteJoined ? callDuration : ((hasRemoteVideo || !isCameraOff) ? 'Video Call' : 'Audio Call')}</span>
                </div>
                {(hasRemoteVideo || !isCameraOff) && (
                    <h3 className="lk-call-top-name">{callerName}</h3>
                )}
            </div>

            {/* Controls */}
            <div className="lk-call-controls">
                <button
                    className={`lk-call-btn ${isMuted ? 'active' : ''}`}
                    onClick={toggleMute}
                    title={isMuted ? 'Unmute' : 'Mute'}
                >
                    {isMuted ? <MicMuteIcon /> : <MicIcon />}
                    <span className="lk-call-btn-label">{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>

                <button
                    className={`lk-call-btn ${isCameraOff ? 'active' : ''}`}
                    onClick={toggleCamera}
                    title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}
                >
                    {isCameraOff ? <CameraOffIcon /> : <CameraIcon />}
                    <span className="lk-call-btn-label">{isCameraOff ? 'Camera On' : 'Camera Off'}</span>
                </button>

                <button
                    className="lk-call-btn end-call"
                    onClick={handleEndCall}
                    title="End call"
                >
                    <PhoneEndIcon />
                    <span className="lk-call-btn-label">End</span>
                </button>
            </div>
        </div>
    );
}
